import { useRef, useState, type FormEvent } from "react";
import { Camera, Loader2 } from "lucide-react";
import { de } from "@ainkauf/core";
import { leseRezept } from "@/lib/assistant";
import { createRecipeFromDraft } from "@/db/repository";
import { alsDatenUrl, verkleinern } from "@/lib/image";
import { supabaseConfigured } from "@/lib/supabase";

/**
 * Ein Rezept übernehmen: Text, Link oder Foto.
 *
 * Ein Feld für alles. Ob dort ein Link steht oder ein abgetippter Zettel,
 * erkennt die App an "http", und niemand muss vorher eine Sorte auswählen.
 */
export function RecipeImportSheet({ onDone }: { onDone: (recipeId: string | null) => void }) {
  const [text, setText] = useState("");
  const [liest, setLiest] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const dateiFeld = useRef<HTMLInputElement>(null);

  async function uebernimm(eingabe: { text?: string; url?: string; image?: string }) {
    setLiest(true);
    setFehler(null);
    try {
      const entwurf = await leseRezept(eingabe);
      if (entwurf.name.trim().length === 0 && entwurf.ingredients.length === 0) {
        setFehler(de.recipes.importFailed);
        return;
      }
      const rezept = await createRecipeFromDraft(entwurf, eingabe.url ?? null);
      onDone(rezept.id);
    } catch {
      // Der genaue Fehler hilft hier niemandem. Wichtig ist der Ausweg:
      // selbst eintippen geht immer.
      setFehler(de.recipes.importFailed);
    } finally {
      setLiest(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const eingabe = text.trim();
    if (eingabe.length === 0) return;
    void uebernimm(/^https?:\/\//iu.test(eingabe) ? { url: eingabe } : { text: eingabe });
  }

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40"
      onClick={() => {
        onDone(null);
      }}
      role="presentation"
    >
      <form
        onSubmit={submit}
        onClick={(event) => {
          event.stopPropagation();
        }}
        aria-label={de.recipes.importTitle}
        data-testid="recipe-import-sheet"
        className="flex flex-col gap-4 rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <h2 className="text-xl">{de.recipes.importTitle}</h2>
        <p className="text-sm text-ink-2">{de.recipes.importHint}</p>

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.recipes.importText}
          <textarea
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
            rows={4}
            placeholder={de.recipes.importTextPlaceholder}
            data-testid="recipe-import-text"
            className="rounded-[var(--radius-lg)] bg-paper-2 px-4 py-3 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
          />
        </label>

        {fehler !== null && <p className="text-sm text-accent">{fehler}</p>}

        {supabaseConfigured() && (
          <>
            <input
              ref={dateiFeld}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              data-testid="recipe-import-file"
              onChange={(event) => {
                const datei = event.target.files?.[0];
                if (datei) void bildLesen(datei);
                event.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => dateiFeld.current?.click()}
              disabled={liest}
              data-testid="recipe-import-photo"
              className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-[var(--radius-lg)] bg-paper-2 font-semibold text-ink-2 disabled:opacity-50"
            >
              <Camera size={18} strokeWidth={1.75} aria-hidden />
              {de.recipes.importPhoto}
            </button>
          </>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              onDone(null);
            }}
            className="min-h-[52px] flex-1 rounded-[var(--radius-lg)] bg-paper-2 font-semibold text-ink-2"
          >
            {de.common.cancel}
          </button>
          <button
            type="submit"
            disabled={liest || text.trim().length === 0}
            data-testid="recipe-import-submit"
            className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-[var(--radius-lg)] bg-accent font-display text-lg font-extrabold text-white disabled:opacity-40"
          >
            {liest && <Loader2 size={18} strokeWidth={2} className="animate-spin" aria-hidden />}
            {liest ? de.recipes.importReading : de.common.save}
          </button>
        </div>
      </form>
    </div>
  );

  async function bildLesen(datei: File) {
    await uebernimm({ image: await alsDatenUrl(await verkleinern(datei)) });
  }
}
